/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import JavascriptStateMachine from 'javascript-state-machine';
import isNil from 'lodash/isNil';

import HtmlMediaUtil from '../../html/html-media-util';
import StateMachine from '../../state-machine/state-machine';
import StateMachineFactory from '../../state-machine/state-machine-factory';
import StretchNative from '../../stretch/stretch-native';
import ViewerLifeCycle from '../../viewer/life-cycle/viewer-life-cycle';

// TODO Sdílí spoustu kódu s ostatními ovladači, DRY.
// - TizenSssp4VideoViewerController
// - WebosSignage3_0StreamViewerController
// TODO Sdílí CSS třídu viewer-controller-video s ostatními ovladači.
// - To je nevhodné z pohledu organizace, kde by mělo být jméno dle třídy.
// - Na druhou stranu aktuální přístup, kdy se třídy odebírají a přidávají
//   při každé změně média se také neosvědčil, protože na krátký okamžik nemusí
//   mít video element žádnou třídu, takže se zbytečně mění jeho rozměry.
// - Zatím tedy ponecháno takto, dokud nedojde k refaktorování na lepší řešení.
/**
 * Ovladač pro zobrazování video souborů.
 * @implements {ViewerController}
 */
class ViewerControllerVideo {
  /**
   * Vytvoří instanci.
   * @param {null|string} poster
   * @returns {ViewerControllerVideo} Nová instance.
   */
  constructor(poster) {
    /**
     * Custom poster pro video element.
     * @private
     * @type {null|string}
     */
    this.poster = poster;

    this.stretchNative = new StretchNative();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    /**
     * @private
     * @type {HtmlMediaUtil}
     */
    this.mediaUtil = new HtmlMediaUtil();
    /**
     * Callback pro selhání spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {?Function}
     */
    this.playReject = null;
    /**
     * Callback pro úspěch spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {?Function}
     */
    this.playResolve = null;
    /**
     * Časovač pro maximální dobu čekání na spuštění přehrávání.
     * @private
     * @type {?number}
     */
    this.playTimeout = null;
    this.positionLastTime = null;
    this.positionTimeout = null;
    this.scheduleInfo = null;
    const stateMachineFactory = new StateMachineFactory(
      JavascriptStateMachine,
      StateMachine
    );
    this.viewerLifeCycle = new ViewerLifeCycle(stateMachineFactory);
    this.viewerSource = null;
    // Navázání naslouchání na instanci.
    this.onCanplaythrough = this.onCanplaythrough.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  bindElement(el) {
    el.addEventListener('canplaythrough', this.onCanplaythrough);
    el.addEventListener('ended', this.onEnded);
    el.addEventListener('error', this.onError);
    el.addEventListener('playing', this.onPlaying);
  }

  /** @inheritdoc */
  destroy() {
    this.stopPositionWatchdog();
    clearTimeout(this.playTimeout);
    const { viewerSource } = this;
    if (!isNil(viewerSource)) {
      const el = viewerSource.getEl();
      this.unbindElement(el);
    }
  }

  /** @inheritdoc */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      const { scheduleInfo, viewerSource } = this;
      // Určení všech potřebných hodnot.
      const volume = scheduleInfo.getVolume();
      const el = viewerSource.getEl();
      const viewerFrame = viewerSource.getFrame();
      // Zobrazení obalujícího elementu.
      viewerFrame.enter();
      // Zapnutí zvuku videa, pouze pokud je potřeba.
      // Zapnutí při volume na 0 může přerušit přehrávání zvuku jiného videa.
      if (volume > 0) {
        el.muted = false;
      }
      // #505 - webOS 6.0 - nefunkční přehrávání videí
      el.style.visibility = 'visible';
      // Zahájení přehrávání videa.
      return this.play(el);
    });
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      const { viewerSource } = this;
      // Určení všech potřebných hodnot.
      const el = viewerSource.getEl();
      const viewerFrame = viewerSource.getFrame();
      // Skrytí obalujícího elementu.
      viewerFrame.leave();
      // Video se zastaví.
      el.pause();
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na dostatečné načtení zdroje v elementu.
   * @private
   * @returns {undefined} Nic.
   */
  onCanplaythrough() {
    const el = this.viewerSource.getEl();
    this.emitter.emit('loaded');
    // Ošetření neočekávaného opakovaného vyvolaní události `canplaythrough`.
    // Dochází k němu v každé iteraci přehrávání při nastavení `loop`.
    // Pozorováno ale i jindy, např. při minimalizaci okna prohlížeče.
    el.removeEventListener('canplaythrough', this.onCanplaythrough);
  }

  /**
   * Dosažení konce přehrávání.
   * @private
   * @returns {undefined} Nic.
   */
  onEnded() {
    this.stopPositionWatchdog();
    this.emitter.emit('played');
  }

  /**
   * Reakce na chybu v elementu.
   *
   * Pokud probíhá pokus o spuštění videa,
   * tak se chybová událost interpretuje jako jeho selhání.
   * V opačném případě se chyba emituje.
   * @private
   * @returns {undefined} Nic.
   */
  onError() {
    this.stopPositionWatchdog();
    const el = this.viewerSource.getEl();
    const error = this.mediaUtil.createError(el);
    const { playReject } = this;
    if (!isNil(playReject)) {
      return playReject(error);
    }
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na skutečný začátek přehrávání.
   * Pokud probíhá pokus o spuštění videa,
   * tak se událost se začátkem přehráváním interpretuje jako jeho dokončení.
   * V opačném případě se nedělá nic.
   * @private
   * @returns {undefined} Nic.
   */
  onPlaying() {
    const { playResolve } = this;
    if (!isNil(playResolve)) {
      return playResolve();
    }
  }

  /**
   * Spustí přehrávání video elementu.
   *
   * Vzhledem ke zpětné kompatibilitě se starými Chrome se naslouchá události `playing`,
   * místo spolehání na `Promise` vrácenou metodou `play`.
   *
   * Je nutné naslouchat na událost `playing`, která informuje o skutečném začátku přehrávání.
   * Událost `play` informuje pouze o začátku pokusu o spuštění.
   * Exerimentálně bylo zjištěno, že stejně se chová i `Promise`,
   * která byla vrácena při volání `HTMLMediaElement.play`.
   *
   * Dokumentace:
   * [HTMLMediaElement.play returns a Promise](https://developers.google.com/web/updates/2016/03/play-returns-promise).
   * @param {HTMLVideoElement} el Element, který se má přehrát.
   * @private
   * @returns {Promise.<undefined>}
   */
  play(el) {
    return new Promise((resolve, reject) => {
      this.playReject = reject;
      this.playResolve = resolve;
      const promise = el.play();
      if (!isNil(promise)) {
        promise.catch((error) => reject(error));
      }
      this.playTimeout = setTimeout(
        () => reject(new Error('Play request timed out.')),
        // TODO Jakou hodnotu zvolit? Přidat do konfigurace.
        30 * 1000
      );
      // Video currentPosition watchdog init
      this.positionLastTime = 0;
      this.startPositionWatchdog();
    }).finally(() => {
      this.playReject = null;
      this.playResolve = null;
      clearTimeout(this.playTimeout);
    });
  }

  /**
   * Spustí position watchdog
   * @private
   * @returns {undefined}
   */
  startPositionWatchdog() {
    this.positionTimeout = setTimeout(this.positionWatchdog.bind(this), 1000);
  }

  /**
   * Zastaví position watchdog
   * @private
   * @returns {undefined}
   */
  stopPositionWatchdog() {
    clearTimeout(this.positionTimeout);
  }

  /**
   * Ochrana proti zaseknutí přehrávání videa kontrolou currentTime.
   * Slouží eliminaci chyb v Chromiu, kdy se video zastaví (event waiting), ale
   * již poté nepokračuje dále v přehrávání.
   * Pozorováno na Chromiu 90 / Gilbarco, nelze spolehlivě simulovat.
   * @private
   * @returns {undefined}
   */
  positionWatchdog() {
    const el = this.viewerSource.getEl();
    if (el.currentTime === this.positionLastTime) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError
      const error = new Error(`Position timeout: ${el.src}`);
      // Zvoleno nejbližší možné MEDIA_ERR_DECODE
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code#media_error_code_constants
      error.code = 3;
      if (isNil(this.playReject)) {
        this.emitter.emit('error', this.mediaUtil.createError({ error }));
      } else {
        this.playReject(error);
      }
      return;
    }
    this.positionLastTime = el.currentTime;
    this.startPositionWatchdog();
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      const { viewerSource } = this;
      // Určení všech potřebných hodnot.
      const el = viewerSource.getEl();
      const viewerFrame = viewerSource.getFrame();
      // Uvolnění obalujícího elementu.
      viewerFrame.release();
      // Uvolnění zdrojového elementu.
      this.unbindElement(el);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
      this.viewerSource = null;
    });
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      this.viewerSource = viewerSource;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const stretchEnum = scheduleInfo.getStretchEnum();
        const stretchClassName = this.stretchNative.getClassName(stretchEnum);
        const volume = scheduleInfo.getVolume();
        const el = viewerSource.getEl();
        const viewerFrame = viewerSource.getFrame();
        // Příprava obalujícího elementu.
        viewerFrame.seize(scheduleInfo);
        // Zahájí se naslouchání událostem z video elementu.
        this.bindElement(el);
        // Nastaví se CSS třídy.
        el.classList.add('viewer-controller-video', stretchClassName);
        // #505 - webOS 6.0 - nefunkční přehrávání videí
        el.style.visibility = 'hidden';
        // Zablokuje se zpracování zvuku preloadovaného videa.
        // Některé platformy podporují přehrávání pouze jednoho zdroje zvuku
        // a bez muted by mohlo zrušit zvuk u jiného videa.
        el.muted = true;
        if (!isNil(this.poster)) {
          // Některé webové platformy (Mersive) nedodržují standard a mají vlastní poster obrázek, vynutí se prázdný.
          // Nastavení tohoto atributu ale rozhodí přehrávání videa u webOS 4.0,
          // proto je vyčleněno do samostatné implementace.
          el.poster = this.poster;
        }
        // Nastaví se hlasitost, ve které se video později přehraje.
        el.volume = volume;
        // Nastaví se zdroj, což zahájí načítání videa.
        el.src = url;
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindElement(el) {
    el.removeEventListener('canplaythrough', this.onCanplaythrough);
    el.removeEventListener('ended', this.onEnded);
    el.removeEventListener('error', this.onError);
    el.removeEventListener('playing', this.onPlaying);
  }
}

export default ViewerControllerVideo;
